'use client'

import { cx } from '@7f-ui'
import { TextExpander } from '@7f-ui/components/text-expander/text-expander'
import type { Asset, MerchantObject } from '@data-access/graphql/schema.types'
import { shopAsset } from '@utils/utils-config'
import { formatMonthYear } from '@utils/utils-format'
import Image from 'next/image'

import { MerchantDetails, type MerchantDetailsProps } from './merchant-details'

export type Merchant = MerchantDetailsProps['merchant'] &
  Pick<MerchantObject, 'memberSince' | 'profileText' | 'tradeName'> & {
    image?: Pick<Asset, 'url' | 'alt'> | null
  }

export type MerchantProfileProps = {
  merchant?: Merchant | null
  onShowImprint?: () => void
}

export function MerchantProfile({ merchant, onShowImprint }: MerchantProfileProps) {
  if (!merchant) return null

  const memberSince =
    merchant.memberSince && `Mitglied seit ${formatMonthYear(merchant.memberSince)}`

  const { url = shopAsset('/images/guide-placeholder.png'), alt = 'platzhalterbild-fuer-guides' } =
    merchant.image ?? {}

  return (
    <div className="flex gap-4">
      <Image
        className="size-16 shrink-0 rounded-xl object-cover"
        src={url}
        alt={alt}
        width={200}
        height={200}
      />
      <div className="flex flex-col align-start">
        <p className="font-body-small font-semibold">
          {`Angeboten von ${merchant.tradeName ?? ''}`}
        </p>

        {memberSince && (
          <p className={cx('font-body-small', merchant.profileText && 'mb-2')}>{memberSince}</p>
        )}

        {!merchant.profileText && (
          <MerchantDetails merchant={merchant} onShowImprint={onShowImprint} />
        )}

        {merchant.profileText && (
          <TextExpander contentSpacing="small">
            {getProfileText(merchant.profileText)?.map((paragraph, index) => (
              <p key={index} className="font-body-small">
                {paragraph} <span className="text-white">.</span>
              </p>
            ))}

            <MerchantDetails merchant={merchant} onShowImprint={onShowImprint} />
          </TextExpander>
        )}
      </div>
    </div>
  )
}

function getProfileText(profileText: string | null | undefined) {
  if (!profileText) return null

  return profileText.split('\n')
}
